import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Tag } from 'lucide-react';

import {
  Modal,
  Button,
  Input,
  Loading,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../components/ui';
import { createRoleSchema, type CreateRoleFormData } from '../../shared/lib/validation-schemas';
import { useCreateRole } from '../../shared/lib/hooks/use-roles';
import { isApiError } from '../../shared/api';
import type { ValidationError } from '../../shared/types';
import { PermissionSelectorSimple as PermissionSelector } from './permission-selector-simple';
import { ErrorBoundary } from '../../components/ui/error-boundary';

interface CreateRoleFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateRoleForm: React.FC<CreateRoleFormProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();

  // Create role mutation
  const createRoleMutation = useCreateRole();

  // Form setup
  const form = useForm<CreateRoleFormData>({
    resolver: zodResolver(createRoleSchema),
    defaultValues: {
      name: '',
      description: '',
      permissions: [],
      serviceName: '',
    },
  });

  // Handle form submission
  const onSubmit = async (data: CreateRoleFormData) => {
    try {
      await createRoleMutation.mutateAsync({
        name: data.name,
        description: data.description || undefined,
        permissions: data.permissions,
        serviceName: data.serviceName || undefined,
      });

      // Reset form and close modal on success
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Create role error:', error);
      
      if (isApiError(error)) {
        if (
          error.isValidationError &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'detail' in error.response.data &&
          Array.isArray(error.response.data.detail)
        ) {
          const validationErrors = error.response.data.detail as ValidationError[];
          
          // Set field-specific errors
          validationErrors.forEach((validationError) => {
            const fieldName = validationError.loc[validationError.loc.length - 1] as string;
            
            // Map backend field names to form field names
            const fieldMapping: Record<string, keyof CreateRoleFormData> = {
              'name': 'name',
              'description': 'description',
              'permissions': 'permissions',
              'service_name': 'serviceName',
            };
            
            const formFieldName = fieldMapping[fieldName] || fieldName as keyof CreateRoleFormData;
            
            if (formFieldName && formFieldName in form.getValues()) {
              form.setError(formFieldName, {
                type: 'manual',
                message: validationError.msg,
              });
            } else {
              // If we can't map the field, show as root error
              form.setError('root', {
                type: 'manual',
                message: `${fieldName}: ${validationError.msg}`,
              });
            }
          });
        } else if (error.isValidationError) {
          // Fallback for validation errors without detailed info
          form.setError('root', {
            type: 'manual',
            message: error.message,
          });
        } else if (error.status === 409) {
          // Role name already exists
          form.setError('name', {
            type: 'manual',
            message: t('errors.roleNameExists', 'Role name already exists'),
          });
        } else {
          form.setError('root', {
            type: 'manual',
            message: t('errors.api', { message: error.message }),
          });
        }
      } else {
        form.setError('root', {
          type: 'manual',
          message: t('errors.unknown'),
        });
      }
    }
  };

  // Handle modal close
  const handleClose = React.useCallback(() => {
    try {
      if (!form.formState.isSubmitting) {
        form.reset();
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Error closing create role form:', error);
      // Force close even if there's an error
      onOpenChange(false);
    }
  }, [form, onOpenChange]);

  const isFormLoading = form.formState.isSubmitting || createRoleMutation.isPending;

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={t('roles.createRole')}
      size="lg"
      className="max-w-2xl w-full mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Role Name Field */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.name')} *</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t('roles.roleName')}
                    disabled={isFormLoading}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t('roles.roleNameDescription', 'Role name can only contain letters, numbers, underscores and hyphens')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Description Field */}
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('common.description')}</FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t('roles.roleDescription')}
                    disabled={isFormLoading}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t('roles.roleDescriptionDescription', 'Optional description to help identify the role purpose')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Service Name Field */}
          <FormField
            control={form.control}
            name="serviceName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  {t('roles.serviceName')}
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder={t('roles.serviceNamePlaceholder', 'e.g., user-service, auth-service')}
                    disabled={isFormLoading}
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  {t('roles.serviceNameDescription', 'Optional service name to scope this role to a specific service. Leave empty for global role.')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Permissions Selection */}
          <FormField
            control={form.control}
            name="permissions"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('roles.permissions')} *</FormLabel>
                <FormControl>
                  <ErrorBoundary fallback={<div className="p-4 text-center text-muted-foreground">Ошибка загрузки селектора разрешений</div>}>
                    <PermissionSelector
                      value={field.value || []}
                      onChange={field.onChange}
                      disabled={isFormLoading}
                    />
                  </ErrorBoundary>
                </FormControl>
                <FormDescription>
                  {t('roles.permissionsDescription', 'Select the permissions this role should have. At least one permission is required.')}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Form-level Error Display */}
          {form.formState.errors.root && (
            <div className="rounded-md bg-destructive/15 p-3">
              <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isFormLoading}
              className="w-full sm:w-auto order-2 sm:order-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isFormLoading}
              className="w-full sm:w-auto order-1 sm:order-2"
            >
              {isFormLoading ? (
                <div className="flex items-center justify-center">
                  <Loading size="sm" className="mr-2" text="" />
                  {t('common.loading')}
                </div>
              ) : (
                t('common.create')
              )}
            </Button>
          </div>
        </form>
      </Form>
    </Modal>
  );
};